import React, { useState } from "react";
import {
  Button,
  Tooltip,
  List,
  Typography,
  Space,
  Modal,
  Image,
  message,
} from "antd";
import {
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileImageOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileUnknownOutlined,
} from "@ant-design/icons";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { BACKEND_URL } from "../../constants";
import PermissionRender from "../../components/PermissionRender";

const { confirm } = Modal;
const { Text } = Typography;

const DocumentPreview = ({ client, onDeleteDocument }) => {
  // Check if client documents are available and parse them into an array
  const documents = client?.documents ? client.documents.split(",") : [];
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewType, setPreviewType] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Supported file types
  const imageExtensions = ["jpg", "jpeg", "png", "gif"];
  const docExtensions = ["doc", "docx"];
  const excelExtensions = ["xls", "xlsx"];
  const textExtensions = ["txt"];
  const pdfExtensions = ["pdf"];

  // Simplified download - just open in new tab
  const handleDownload = (doc) => {
    const url = BACKEND_URL + `/clients/${doc}`;
    window.open(url, "_blank");
  };

  const showDeleteConfirm = (doc) => {
    confirm({
      title: "Êtes-vous sûr de vouloir supprimer ce document?",
      icon: <ExclamationCircleOutlined />,
      content: `Document: ${doc.split("/").pop()}`,
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk() {
        if (onDeleteDocument) {
          onDeleteDocument(doc);
        }
      },
    });
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();

    if (imageExtensions.includes(extension)) {
      return <FileImageOutlined className="text-green-500 mr-2 text-lg" />;
    } else if (pdfExtensions.includes(extension)) {
      return <FilePdfOutlined className="text-red-500 mr-2 text-lg" />;
    } else if (textExtensions.includes(extension)) {
      return <FileTextOutlined className="text-blue-500 mr-2 text-lg" />;
    } else if (docExtensions.includes(extension)) {
      return <FileWordOutlined className="text-blue-700 mr-2 text-lg" />;
    } else if (excelExtensions.includes(extension)) {
      return <FileExcelOutlined className="text-green-700 mr-2 text-lg" />;
    } else {
      return <FileUnknownOutlined className="text-gray-500 mr-2 text-lg" />;
    }
  };

  const handlePreview = (doc) => {
    const fileName = doc.split("/").pop();
    const extension = fileName.split(".").pop().toLowerCase();
    const fileUrl = BACKEND_URL + `/clients/${doc}`;

    setPreviewTitle(fileName);
    setIsLoading(true);

    try {
      if (imageExtensions.includes(extension)) {
        setPreviewType("image");
        setPreviewContent(fileUrl);
        setPreviewVisible(true);
      } else if (pdfExtensions.includes(extension)) {
        setPreviewType("pdf");
        setPreviewContent(fileUrl);
        setPreviewVisible(true);
      } else if (textExtensions.includes(extension)) {
        // For text files, we'll use a new window
        window.open(fileUrl, "_blank");
      } else {
        // For other file types, open in new tab
        window.open(fileUrl, "_blank");
      }
    } catch (error) {
      console.error("Error previewing file:", error);
      message.error(
        "Impossible d'afficher l'aperçu. Veuillez essayer à nouveau."
      );
      window.open(fileUrl, "_blank");
    } finally {
      setIsLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewVisible(false);
    setPreviewContent(null);
  };

  const renderPreviewContent = () => {
    switch (previewType) {
      case "image":
        return (
          <div className="flex justify-center">
            <Image
              alt={previewTitle}
              src={previewContent}
              style={{ maxWidth: "100%", maxHeight: "70vh" }}
              preview={false}
            />
          </div>
        );
      case "pdf":
        return (
          <div
            className="pdf-container"
            style={{ height: "70vh", width: "100%" }}
          >
            <object
              data={previewContent}
              type="application/pdf"
              width="100%"
              height="100%"
              className="rounded-md"
            >
              <div className="flex flex-col items-center justify-center p-4 bg-gray-100 rounded-md h-full">
                <p className="mb-4 text-center">
                  Le navigateur ne peut pas afficher ce PDF. Vous pouvez
                  l'ouvrir dans un nouvel onglet.
                </p>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    const docPath = previewContent.replace(
                      BACKEND_URL + "/clients/",
                      ""
                    );
                    handleDownload(docPath);
                  }}
                >
                  Ouvrir le PDF
                </Button>
              </div>
            </object>
          </div>
        );
      default:
        return <div className="text-center py-8">Aperçu non disponible</div>;
    }
  };

  const canPreview = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    return [...imageExtensions, ...pdfExtensions, ...textExtensions].includes(
      extension
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      {documents.length > 0 ? (
        <div>
          <h3 className="text-gray-700 font-medium text-lg mb-3">Documents:</h3>
          <List
            itemLayout="horizontal"
            dataSource={documents}
            renderItem={(doc, index) => {
              const fileName = doc.split("/").pop();
              const showPreviewButton = canPreview(fileName);
              const extension = fileName.split(".").pop().toLowerCase();

              return (
                <List.Item
                  className="border rounded-md hover:bg-gray-50 transition-colors px-4 py-2 mb-2"
                  key={index}
                >
                  <div className="w-full flex items-center justify-between">
                    <div className="flex items-center">
                      {getFileIcon(fileName)}
                      <Text ellipsis className="max-w-[200px] md:max-w-xs">
                        {fileName}
                      </Text>
                    </div>
                    <Space>
                      {showPreviewButton && (
                        <Tooltip
                          title={
                            textExtensions.includes(extension)
                              ? "Ouvrir"
                              : "Aperçu"
                          }
                        >
                          <Button
                            type="default"
                            shape="circle"
                            icon={<EyeOutlined />}
                            size="middle"
                            onClick={() => handlePreview(doc)}
                            loading={isLoading}
                          />
                        </Tooltip>
                      )}
                      <Tooltip title="Ouvrir dans un nouvel onglet">
                        <Button
                          type="primary"
                          shape="circle"
                          icon={<DownloadOutlined />}
                          size="middle"
                          className="bg-blue-500 hover:bg-blue-600"
                          onClick={() => handleDownload(doc)}
                        />
                      </Tooltip>
                      <PermissionRender
                        requiredPermission={["clients", "update"]}
                      >
                        <Tooltip title="Supprimer">
                          <Button
                            type="primary"
                            danger
                            shape="circle"
                            icon={<DeleteOutlined />}
                            size="middle"
                            onClick={() => showDeleteConfirm(doc)}
                          />
                        </Tooltip>
                      </PermissionRender>
                    </Space>
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      ) : (
        <div className="text-center py-6 border rounded-md bg-gray-50">
          <Text className="text-gray-500">Aucun document disponible</Text>
        </div>
      )}

      {/* Preview Modal */}
      <Modal
        title={previewTitle}
        open={previewVisible}
        onCancel={closePreview}
        footer={[
          <Button key="close" onClick={closePreview}>
            Fermer
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              const docPath = previewContent?.replace(
                BACKEND_URL + "/clients/",
                ""
              );
              if (docPath) {
                handleDownload(docPath);
              }
            }}
          >
            Ouvrir
          </Button>,
        ]}
        width="80%"
        style={{ top: 20 }}
        bodyStyle={{ padding: previewType === "image" ? "16px" : "16px" }}
      >
        {renderPreviewContent()}
      </Modal>
    </div>
  );
};

export default DocumentPreview;
